import { useEffect, useRef } from 'react';

type Props = { base: string; chrome: string; alt: string; className?: string };

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/** Liquid reveal: the overlay layer shows through a soft pointer trail over the real portrait. */
export default function LiquidPortrait({ base, chrome, alt, className = '' }: Props) {
  const wrap = useRef<HTMLDivElement>(null);
  const top = useRef<HTMLImageElement>(null);
  const fringe = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const el = wrap.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const fine = window.matchMedia('(pointer: fine)').matches;
    const TRAIL = fine ? 12 : 7;
    const target = { x: 0.5, y: 0.45 };
    const pos = { x: 0.5, y: 0.45 };
    const trail = Array.from({ length: TRAIL }, () => ({ x: 0.5, y: 0.45 }));
    let lastInput = 0;
    let raf = 0;
    let frame = 0;
    let visible = true;
    const t0 = performance.now();

    const onMove = (e: PointerEvent) => {
      const r = el.getBoundingClientRect();
      target.x = (e.clientX - r.left) / r.width;
      target.y = (e.clientY - r.top) / r.height;
      lastInput = performance.now();
    };
    el.addEventListener('pointermove', onMove, { passive: true });

    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible && !raf) raf = requestAnimationFrame(tick);
    }, { threshold: 0.05 });
    io.observe(el);

    const paint = (img: HTMLImageElement | null, grow: number, strength: number) => {
      if (!img) return;
      const m = trail
        .map((p, i) => {
          const r = (34 - i * 1.6) * strength * grow;
          return `radial-gradient(circle at ${(p.x * 100).toFixed(1)}% ${(p.y * 100).toFixed(1)}%, black ${(r * 0.45).toFixed(1)}%, transparent ${r.toFixed(1)}%)`;
        })
        .join(',');
      img.style.maskImage = m;
      img.style.webkitMaskImage = m;
    };

    const tick = (now: number) => {
      if (!visible) {
        raf = 0;
        return;
      }
      frame++;
      const t = (now - t0) / 1000;
      const idle = now - lastInput > 1600;
      if (idle && frame % 2) {
        raf = requestAnimationFrame(tick);
        return;
      }
      if (idle) {
        target.x = 0.5 + Math.sin(t * 0.55) * 0.22;
        target.y = 0.42 + Math.cos(t * 0.37) * 0.18;
      }
      pos.x = lerp(pos.x, target.x, idle ? 0.08 : 0.18);
      pos.y = lerp(pos.y, target.y, idle ? 0.08 : 0.18);
      for (let i = trail.length - 1; i > 0; i--) {
        trail[i].x = lerp(trail[i].x, trail[i - 1].x, 0.42);
        trail[i].y = lerp(trail[i].y, trail[i - 1].y, 0.42);
      }
      trail[0].x = pos.x;
      trail[0].y = pos.y;
      const strength = idle ? 0.62 : 1;
      paint(top.current, 1, strength);
      if (fine && fringe.current) {
        paint(fringe.current, 1.1, strength);
        fringe.current.style.transform = `translate(calc(-50% + ${((pos.x - 0.5) * 6).toFixed(1)}px), ${((pos.y - 0.5) * 4).toFixed(1)}px)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      el.removeEventListener('pointermove', onMove);
    };
  }, []);

  const img = 'absolute bottom-0 left-1/2 h-full w-auto max-w-none -translate-x-1/2 object-contain object-bottom';
  return (
    <div ref={wrap} className={`absolute inset-0 touch-none ${className}`} data-testid="liquid-portrait">
      <img src={base} alt={alt} decoding="async" fetchPriority="high" className={`${img} drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)]`} data-testid="liquid-portrait-base" />
      <img ref={fringe} src={chrome} alt="" aria-hidden decoding="async" className={`${img} hidden opacity-35 mix-blend-screen saturate-[1.6] [mask-image:none] [@media(pointer:fine)]:block`} />
      <img ref={top} src={chrome} alt="" aria-hidden decoding="async" className={`${img} will-change-[mask-image]`} data-testid="liquid-portrait-chrome" />
    </div>
  );
}
